// Het visuele blok — BUITEN de thumbnail (owner-besluit 26-08: "die thumbnail
// cards met graphs, trends met progress bar of skills met visual zouden we hier
// niet doen"). Het leeft nu alleen in de opengeklikte kaart, waar ruimte is en
// waar het bewijs hoort.
//
// Er wordt nooit een beeld getekend dat niet gemeten is: een reeks heeft minstens
// twee punten nodig, anders is de lijn een verzinsel.

import { color, size, weight } from "../tokens";
import type { Visueel } from "../lib/kaartas";

function Sparkline({ punten }: { punten: { periode: string; waarde: number }[] }) {
  const waarden = punten.map((p) => p.waarde);
  const min = Math.min(...waarden);
  const max = Math.max(...waarden);
  const bereik = max - min || 1;
  const b = 520;
  const h = 76;
  const pad = 8;
  const pad_ = punten
    .map((p, i) => {
      const x = (i / (punten.length - 1)) * b;
      const y = h - pad - ((p.waarde - min) / bereik) * (h - pad * 2);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
  const laatste = punten[punten.length - 1]!;
  const ly = h - pad - ((laatste.waarde - min) / bereik) * (h - pad * 2);
  return (
    <svg
      viewBox={`0 0 ${b} ${h}`}
      width="100%"
      height={h}
      role="img"
      aria-label={`Reeks van ${punten.length} meetpunten, laatste waarde ${laatste.waarde}`}
      style={{ display: "block" }}
    >
      <line x1="0" y1={h - pad} x2={b} y2={h - pad} stroke={color.lineFaint} strokeWidth="1" />
      <line x1="0" y1={pad} x2={b} y2={pad} stroke={color.lineFaint} strokeWidth="1" />
      <polyline
        fill="none"
        stroke={color.accentAmber}
        strokeWidth="1.8"
        strokeLinejoin="round"
        strokeLinecap="round"
        points={pad_}
      />
      <circle cx={b} cy={ly} r="3.2" fill={color.accentAmber} />
    </svg>
  );
}

function Balk({ label, waarde, kleur }: { label: string; waarde: number; kleur: string }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "84px 1fr 34px", gap: 10, alignItems: "center" }}>
      <span style={{ fontSize: size.label, color: color.faint }}>{label}</span>
      <span style={{ height: 5, background: color.border, borderRadius: 99, display: "block", position: "relative" }}>
        <span
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: `${Math.max(0, Math.min(100, waarde * 10))}%`,
            background: kleur,
            borderRadius: 99,
          }}
        />
      </span>
      <span
        style={{
          fontSize: size.label,
          color: color.body,
          textAlign: "right",
          fontWeight: weight.medium,
        }}
      >
        {waarde.toFixed(1).replace(".", ",")}
      </span>
    </div>
  );
}

export function VisueelBlok({ visueel }: { visueel: Visueel }) {
  if (!visueel) return null;

  if (visueel.soort === "reeks") {
    const delta =
      visueel.eerder != null && visueel.eerder !== 0
        ? ((visueel.waarde - visueel.eerder) / visueel.eerder) * 100
        : null;
    return (
      <div>
        <div style={{ display: "flex", gap: 14, alignItems: "baseline", marginBottom: 6 }}>
          <span style={{ fontSize: 26, fontWeight: weight.bold, color: color.ink, lineHeight: 1 }}>
            {String(visueel.waarde).replace(".", ",")}
          </span>
          {visueel.eerder != null && (
            <span style={{ fontSize: size.meta, color: color.faint }}>
              jaar eerder {String(visueel.eerder).replace(".", ",")}
              {delta != null && (
                <span style={{ color: color.accentAmber, fontWeight: weight.medium }}>
                  {" "}
                  {delta > 0 ? "+" : ""}
                  {delta.toFixed(1).replace(".", ",")}%
                </span>
              )}
            </span>
          )}
        </div>
        <Sparkline punten={visueel.punten} />
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            fontSize: size.label,
            color: color.faint,
            marginTop: 3,
          }}
        >
          <span>{visueel.punten[0]?.periode.slice(0, 7)}</span>
          <span>{visueel.punten[visueel.punten.length - 1]?.periode.slice(0, 7)}</span>
        </div>
      </div>
    );
  }

  if (visueel.soort === "scores") {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
        {visueel.prioriteit != null && <Balk label="Prioriteit" waarde={visueel.prioriteit} kleur={color.ink} />}
        {visueel.impact != null && <Balk label="Impact" waarde={visueel.impact} kleur={color.soft} />}
        {visueel.effort != null && (
          <Balk label="Inspanning" waarde={visueel.effort} kleur={color.accentGreen} />
        )}
      </div>
    );
  }

  // Een datum of een telling is geen grafiek: die staan al in de tekst en in de
  // bewijsregel, en verdienen geen eigen blok.
  return null;
}
